import { Order, OrderType, OrderSide } from '#/api/orders';
import { Market } from '#/api/markets';

import * as MarketFn from '#/features/market/functions';

import { shortDateFormat, timeFormat } from '#/utils/date';
import capitalizeText from '#/utils/capitalizeText';

type InstructionView = 'Post Only' | 'GTC' | 'IOC';

export interface OrderView {
  readonly id: Order['id'];
  readonly clientId: string;
  readonly createdTime: string;
  readonly createdDate: string;
  readonly market: Market['symbol'];
  readonly type: OrderType;
  readonly side: OrderSide;
  readonly price: string;
  readonly size: string;
  readonly remainingSize: string;
  readonly status: string;
  readonly cancelReason: string;
  readonly instruction: InstructionView;
}

export interface OpenOrderView {
  readonly id: Order['id'];
  readonly clientId: string;
  readonly createdTime: string;
  readonly createdDate: string;
  readonly market: Market['symbol'];
  readonly type: OrderType;
  readonly side: OrderSide;
  readonly price: string;
  readonly size: string;
  readonly filledSize: string;
  readonly instruction: InstructionView;
}

export function baseCurrencyDecimalPlacesView(market?: Market): number {
  if (market == null) return 0;
  return MarketFn.baseCurrencyDecimalPlaces(market);
}

export function quoteCurrencyDecimalPlacesView(market?: Market): number {
  if (market == null) return 0;
  return MarketFn.quoteCurrencyDecimalPlaces(market);
}

export function createdTimeView(order: Order): string {
  return timeFormat(order.created_at);
}

export function createdDateView(order: Order): string {
  return shortDateFormat(order.created_at);
}

export function clientIdView(order: Order): string {
  if (order.client_id == null) return '-';
  if (order.client_id.length === 0) return '-';
  return order.client_id;
}

export function typeView(order: Order): OrderType {
  return capitalizeText(order.type) as OrderType;
}

export function instructionView(order: Order): InstructionView {
  if (order.instruction === 'POST_ONLY') return 'Post Only';
  return order.instruction;
}

export function priceView(order: Order, market?: Market): string {
  if (market == null) return '-';
  if (order.type === 'MARKET') return '-';

  const quoteCurrencyDecimalPlaces = quoteCurrencyDecimalPlacesView(market);
  return order.price.toFormat(quoteCurrencyDecimalPlaces);
}

export function sizeView(order: Order, market?: Market): string {
  if (market == null) return '-';
  const baseCurrencyDecimalPlaces = baseCurrencyDecimalPlacesView(market);
  return `${order.size.toFormat(baseCurrencyDecimalPlaces)} ${
    market.base_currency
  }`;
}

export function remainingSizeView(order: Order, market?: Market): string {
  if (market == null) return '-';
  const baseCurrencyDecimalPlaces = baseCurrencyDecimalPlacesView(market);
  return `${order.remaining_size.toFormat(baseCurrencyDecimalPlaces)} ${
    market.base_currency
  }`;
}

export function filledSizeView(order: Order, market?: Market): string {
  if (market == null) return '-';
  const baseCurrencyDecimalPlaces = baseCurrencyDecimalPlacesView(market);
  return `${order.size
    .minus(order.remaining_size)
    .toFormat(baseCurrencyDecimalPlaces)} ${market.base_currency}`;
}

export function cancelReasonView(order: Order): string {
  if (order.cancel_reason == null) return '-';
  return order.cancel_reason;
}

export function statusView(order: Order): string {
  return capitalizeText(order.status);
}
